import { Link2 } from 'lucide-react';
import { useState } from 'react';

import { EstablishLinkModal } from '../../../../components/EstablishLinkModal';
import { AuthorizedContactsDrawer } from '../../../../features/bonds/authorized-contacts/AuthorizedContactsDrawer';
import { useCanAccessAuthorizedContacts } from '../../../../features/bonds/can-access-authorized-contacts';
import { roleLabel } from '../../../../features/bonds/participants/participants-labels';
import { ParticipantsDrawer } from '../../../../features/bonds/participants/ParticipantsDrawer';
import {
  relationshipLabel,
  sourceLabel,
} from '../../../../features/bonds/related-persons/related-persons-labels';
import { RelatedPersonsDrawer } from '../../../../features/bonds/related-persons/RelatedPersonsDrawer';
import {
  useBond,
  useBondAuthorizedContacts,
  useBondParticipants,
  useBondRelatedPersons,
} from '../../../../hooks/use-bond';
import { useCapability } from '../../../../hooks/use-capability';
import { channelLabel } from '../../format';
import { useCustomerContext } from '../../hooks/use-customer-context';
import { useRefreshFromErp } from '../../hooks/use-refresh-from-erp';

import { ContextBlock } from './ContextBlock';
import { PanelSection } from './PanelSection';

import type { ConversationDetail } from '../../../../lib/types';

/**
 * Aba "Cliente" — identidade + canal + Bond (Fatia B + B-bis).
 *
 * Bond consumido via `GET /v1/bonds/:bondId` + 3 sub-recursos ortogonais
 * (participants ADR-130 + related-persons ADR-129 + authorized-contacts).
 *
 * Capability gate `bond-basic` (Solo+): defense-in-depth — sem capability
 * o bloco Bond é ocultado preservando Identidade. Backend também valida
 * `@RequiresCapability('bond-basic')` em cada endpoint. Quando 403 retorna
 * (recurso fora do alcance — Item 88), `api-client` dispara toast e o
 * hook expõe `isError` para empty state.
 */
export function TabCliente({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const canReadBond = useCapability('bond-basic');
  const bondId = conversation.bondId;
  const { data: context, isLoading: contextLoading } = useCustomerContext(conversation.id);
  const refresh = useRefreshFromErp(conversation.id);
  const personId = conversation.person?.id;

  return (
    <div className="flex flex-col">
      <PanelSection title="Identidade">
        <dl className="kv-list">
          <KvRow label="Nome" value={conversation.person?.displayName ?? '—'} />
          <KvRow
            label="Identificador"
            value={
              conversation.identity !== null
                ? `${channelLabel(conversation.identity.channel)} · ${conversation.identity.channelIdentifier}`
                : channelLabel(conversation.channel)
            }
          />
        </dl>
      </PanelSection>

      <PanelSection title="Contexto do cliente" eyebrow="dados internos e de sistemas externos">
        {contextLoading || context === undefined ? (
          <p className="placeholder-text">Carregando contexto…</p>
        ) : (
          <ContextBlock
            context={context}
            identity={conversation.identity}
            channel={conversation.channel}
            onRefresh={personId !== undefined ? () => refresh.mutate(personId) : undefined}
            refreshing={refresh.isPending}
          />
        )}
      </PanelSection>

      {canReadBond && (
        <PanelSection title="Vínculo (Bond)">
          <BondSection
            bondId={bondId}
            conversationId={conversation.id}
            personName={conversation.person?.displayName ?? 'este contato'}
          />
        </PanelSection>
      )}

      <PanelSection title="Indicadores de origem" eyebrow="reservado">
        <p className="placeholder-text">Disponível em breve.</p>
      </PanelSection>
    </div>
  );
}

/**
 * Bloco "Vínculo (Bond)" — renderização condicional.
 *
 * - bondId null → estado vazio explícito ("conversa sem Bond vinculado").
 * - loading → shimmer textual.
 * - erro → mensagem neutra (toast já tratou 403 via onForbidden).
 * - sucesso → tipo + status + participants + related-persons +
 *   authorized-contacts.
 */
function BondSection({
  bondId,
  conversationId,
  personName,
}: {
  bondId: string | null;
  conversationId: string;
  personName: string;
}): JSX.Element {
  const bond = useBond(bondId);
  const participants = useBondParticipants(bondId);
  const relatedPersons = useBondRelatedPersons(bondId);
  // Item 350 — em tenant restritivo o atendente perde read+manage dos contatos
  // autorizados; esconde o bloco INTEIRO e NÃO dispara o GET (que daria 403 →
  // toast/"Indisponível"). Participantes/Pessoas relacionadas seguem em bond-basic.
  const canAccessContacts = useCanAccessAuthorizedContacts();
  const authorizedContacts = useBondAuthorizedContacts(canAccessContacts ? bondId : null);
  // F2-a — estabelecer o vínculo que falta (fecha o 409). Gated (opt-in).
  const canEstablishLink = useCapability('customer-link-establish');
  const [establishOpen, setEstablishOpen] = useState(false);
  const [managingContacts, setManagingContacts] = useState(false);
  const [managingRelated, setManagingRelated] = useState(false);
  const [managingParticipants, setManagingParticipants] = useState(false);

  if (bondId === null) {
    return (
      <div className="empty-mini flex flex-col items-start gap-2">
        <span className="flex items-center gap-1.5">
          <Link2 className="icon" size={14} aria-hidden />
          Nenhum vínculo (Bond) nesta conversa.
        </span>
        {canEstablishLink && (
          <>
            <button
              type="button"
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--brand)' }}
              onClick={() => setEstablishOpen(true)}
            >
              Estabelecer vínculo
            </button>
            {/* Monta só quando aberto: os hooks (React Query) do modal não rodam
                enquanto fechado — não busca candidatos à toa nem exige contexto. */}
            {establishOpen && (
              <EstablishLinkModal
                conversationId={conversationId}
                personName={personName}
                open
                onClose={() => setEstablishOpen(false)}
              />
            )}
          </>
        )}
      </div>
    );
  }

  if (bond.isLoading) {
    return <p className="placeholder-text">Carregando vínculo…</p>;
  }

  if (bond.isError || bond.data === undefined) {
    return <p className="placeholder-text">Não foi possível carregar o vínculo.</p>;
  }

  const activeParticipants = participants.data.filter((p) => p.revokedAt === null);
  const activeRelatedPersons = relatedPersons.data.filter((rp) => rp.status === 'active');
  const activeAuthorizedContacts = authorizedContacts.data.filter((c) => c.status === 'active');

  return (
    <section>
      <dl className="kv-list">
        <KvRow label="Tipo" value={bondTypeLabel(bond.data.type)} />
        <KvRow label="Status" value={bondStatusLabel(bond.data.status)} />
        {bond.data.adversarialMode !== 'none' && (
          <KvRow label="Adversarial" value={adversarialLabel(bond.data.adversarialMode)} />
        )}
        {bond.data.side !== null && <KvRow label="Lado" value={sideLabel(bond.data.side)} />}
      </dl>

      <BondCountBlock
        label="Participantes"
        items={activeParticipants}
        isLoading={participants.isLoading}
        isError={participants.isError}
        headerAction={
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            onClick={() => setManagingParticipants(true)}
          >
            Gerenciar
          </button>
        }
        render={(p) => (
          <li key={p.id} className="text-xs flex justify-between gap-2">
            <span className="text-text-primary truncate">{roleLabel(p.role)}</span>
            <span className="text-text-secondary text-2xs">
              {p.permissions.length} permiss{p.permissions.length === 1 ? 'ão' : 'ões'}
            </span>
          </li>
        )}
      />

      <BondCountBlock
        label="Pessoas relacionadas"
        items={activeRelatedPersons}
        isLoading={relatedPersons.isLoading}
        isError={relatedPersons.isError}
        headerAction={
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            onClick={() => setManagingRelated(true)}
          >
            Gerenciar
          </button>
        }
        render={(rp) => (
          <li key={rp.id} className="text-xs flex justify-between gap-2">
            <span className="text-text-primary truncate">
              {relationshipLabel(rp.relationshipType)}
            </span>
            {rp.source !== null && (
              <span className="text-text-secondary text-2xs">{sourceLabel(rp.source)}</span>
            )}
          </li>
        )}
      />

      {canAccessContacts && (
        <BondCountBlock
          label="Contatos autorizados"
          items={activeAuthorizedContacts}
          isLoading={authorizedContacts.isLoading}
          isError={authorizedContacts.isError}
          headerAction={
            <button
              type="button"
              className="btn btn--secondary btn--xs"
              onClick={() => setManagingContacts(true)}
            >
              Gerenciar
            </button>
          }
          render={(c) => (
            <li key={c.id} className="text-xs flex justify-between gap-2">
              <span className="text-text-primary truncate">
                {c.scopes.length} escopo{c.scopes.length === 1 ? '' : 's'}
              </span>
              {c.expiresAt !== null && <span className="text-text-secondary text-2xs">expira</span>}
            </li>
          )}
        />
      )}

      <ParticipantsDrawer
        bondId={managingParticipants ? bondId : null}
        onClose={() => setManagingParticipants(false)}
      />

      <RelatedPersonsDrawer
        bondId={managingRelated ? bondId : null}
        onClose={() => setManagingRelated(false)}
      />

      {canAccessContacts && (
        <AuthorizedContactsDrawer
          bondId={managingContacts ? bondId : null}
          onClose={() => setManagingContacts(false)}
        />
      )}
    </section>
  );
}

interface BondCountBlockProps<T> {
  label: string;
  items: T[];
  isLoading: boolean;
  isError: boolean;
  render: (item: T) => JSX.Element;
  headerAction?: JSX.Element;
}

function BondCountBlock<T>({
  label,
  items,
  isLoading,
  isError,
  render,
  headerAction,
}: BondCountBlockProps<T>): JSX.Element {
  return (
    <div className="mt-3">
      <h4 className="text-2xs uppercase tracking-wide font-medium text-text-secondary mb-1 flex items-center justify-between gap-2">
        <span>
          {label} {items.length > 0 && <span className="opacity-70">({items.length})</span>}
        </span>
        {headerAction}
      </h4>
      {isLoading && <p className="text-xs text-text-secondary opacity-60">Carregando…</p>}
      {!isLoading && isError && (
        <p className="text-xs text-text-secondary opacity-70">Indisponível.</p>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <p className="text-xs text-text-secondary opacity-60">Nenhum cadastrado.</p>
      )}
      {!isLoading && !isError && items.length > 0 && (
        <ul className="space-y-0.5">{items.map(render)}</ul>
      )}
    </div>
  );
}

function KvRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="kv-list__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Labels PT-BR — i18n inline (Fatia 3 simples). Migrar a i18next se ≥2 idiomas. */
function bondTypeLabel(type: string): string {
  // 21 tipos canônicos ADR-037/131. Mantém kebab-case original com fallback
  // para tipos desconhecidos (forward-compat se tenant declarar tipo novo
  // antes do frontend ser atualizado).
  const map: Record<string, string> = {
    contract: 'Contrato',
    'customer-account': 'Conta de cliente',
    opportunity: 'Oportunidade',
    procurement: 'Aquisição',
    project: 'Projeto',
    'legal-case': 'Processo judicial',
    mediation: 'Mediação',
    'compliance-case': 'Conformidade',
    'patient-care': 'Cuidado clínico',
    student: 'Estudante',
    'social-care-case': 'Assistência social',
    'home-care': 'Cuidado domiciliar',
    property: 'Imóvel',
    vehicle: 'Veículo',
    equipment: 'Equipamento',
    partnership: 'Parceria',
    'vendor-relationship': 'Fornecedor',
    interest: 'Interesse',
    'purchase-intent': 'Intenção de compra',
    'transaction-attempt': 'Tentativa de transação',
    'rental-contract': 'Locação',
  };
  return map[type] ?? type;
}

function bondStatusLabel(status: 'active' | 'paused' | 'closed' | 'archived'): string {
  const map: Record<typeof status, string> = {
    active: 'Ativo',
    paused: 'Pausado',
    closed: 'Encerrado',
    archived: 'Arquivado',
  };
  return map[status];
}

function adversarialLabel(mode: 'none' | 'competitive' | 'partial' | 'strict'): string {
  const map: Record<typeof mode, string> = {
    none: 'Nenhum',
    competitive: 'Competitivo',
    partial: 'Parcial',
    strict: 'Estrito',
  };
  return map[mode];
}

function sideLabel(side: 'seller' | 'buyer' | 'neutral' | 'mediator'): string {
  const map: Record<typeof side, string> = {
    seller: 'Vendedor',
    buyer: 'Comprador',
    neutral: 'Neutro',
    mediator: 'Mediador',
  };
  return map[side];
}
